import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Each page's title, description and canonical link, for search engines and
// for the browser tab. The app is one index.html, so without this every page
// would share index.html's title and description in Google's results.
// Paths here are the router's, without the /uk basename.

export const SITE_URL = 'https://trackmystreet.com/uk';
const SUFFIX = ' | trackmystreet';
const DEFAULT_DESCRIPTION = 'Free interactive UK property data on a map: house prices, '
    + 'rents, crime, schools, transport and neighbourhood statistics for every street.';

// Pages Google shouldn't list: accounts, payments and reports for one person
const PRIVATE = /^\/(log-in|signup|confirm|forgot-password|reset-password|change-password|profile|subscribe|buy|buy-report|your-subscription|payment-done|reports|shortlist|locations|listing|area-report)(\/|$)/;

// /property/B32-3NH -> 'B32 3NH'
const postcodeOf = (part) => decodeURIComponent(part).replace(/-/g, ' ').toUpperCase();

const PAGES = [
    [/^\/$/, () => ({
        title: 'trackmystreet: UK house prices, crime, schools and area data on a map',
        description: DEFAULT_DESCRIPTION,
    })],
    [/^\/map$/, () => ({
        title: 'Interactive UK property map: prices, crime, schools and more' + SUFFIX,
        description: 'Explore house prices, sales, rents, crime, schools, transport, noise, '
            + 'flood risk and census data for any street in the UK on one map.',
    })],
    [/^\/big-picture$/, () => ({
        title: 'UK property maps: house prices, rents, crime and deprivation by area' + SUFFIX,
        description: 'Whole-country maps of house prices, rents, crime, deprivation, sales '
            + 'and migration by local authority, with how each has changed.',
    })],
    [/^\/property$/, () => ({
        title: 'Property lookup: house prices and values by postcode' + SUFFIX,
        description: 'Enter a postcode to see its homes, what they sold for, estimated '
            + 'values and the area around them.',
    })],
    [/^\/property\/([^/]+)$/, ([, pc]) => ({
        title: `${postcodeOf(pc)}: house prices, sold prices and home values` + SUFFIX,
        description: `Homes in ${postcodeOf(pc)}: sold prices, estimated values, sizes, `
            + 'energy ratings and the local area, including crime, schools and transport.',
    })],
    [/^\/property\/([^/]+)\/[^/]+$/, ([, pc]) => ({
        title: `Home in ${postcodeOf(pc)}: value and sales history` + SUFFIX,
        description: `Estimated value, sales history and local area data for a home in ${postcodeOf(pc)}.`,
        noindex: true, // the home itself is for logged in users only
    })],
    [/^\/faq$/, () => ({
        title: 'Frequently asked questions' + SUFFIX,
        description: 'Answers to common questions about trackmystreet: the data, plans, '
            + 'reports and accounts.',
    })],
    [/^\/terms$/, () => ({ title: 'Terms of use' + SUFFIX })],
    [/^\/privacy$/, () => ({ title: 'Privacy policy' + SUFFIX })],
    [/^\/cookies$/, () => ({ title: 'Cookie policy' + SUFFIX })],
    // Kept out of search (PRIVATE below), but named for the browser tab
    [/^\/(log-in|signup)$/, () => ({ title: 'Log in or sign up' + SUFFIX })],
    [/^\/(subscribe|buy|buy-report)$/, () => ({ title: 'Plans and reports' + SUFFIX })],
    [/^\/reports$/, () => ({ title: 'My reports' + SUFFIX })],
    [/^\/profile$/, () => ({ title: 'Your profile' + SUFFIX })],
];

export const pageMeta = (pathname) => {
    const path = pathname.replace(/\/+$/, '') || '/';
    let meta = { title: 'trackmystreet', noindex: true };
    for (const [pattern, make] of PAGES) {
        const match = path.match(pattern);
        if (match) {
            meta = make(match);
            break;
        }
    }
    if (PRIVATE.test(path)) meta = { ...meta, noindex: true };
    return {
        description: DEFAULT_DESCRIPTION,
        noindex: false,
        ...meta,
        // Without the query string: a map share link is the same page as /map
        canonical: SITE_URL + (path === '/' ? '/' : path),
    };
};

// Finds the head tag, making it if index.html lacks it
const headTag = (selector, make) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = make();
        document.head.appendChild(el);
    }
    return el;
};

const setMeta = (name, content) => {
    const el = headTag(`meta[name="${name}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute('name', name);
        return m;
    });
    el.setAttribute('content', content);
};

const setProperty = (property, content) => {
    const el = headTag(`meta[property="${property}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute('property', property);
        return m;
    });
    el.setAttribute('content', content);
};

// Placed once, beside the routes (AppRoutes.js): updates the head on each page change
export const PageMeta = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const meta = pageMeta(pathname);
        document.title = meta.title;
        setMeta('description', meta.description);
        setMeta('robots', meta.noindex ? 'noindex' : 'index, follow');
        headTag('link[rel="canonical"]', () => {
            const l = document.createElement('link');
            l.setAttribute('rel', 'canonical');
            return l;
        }).setAttribute('href', meta.canonical);
        setProperty('og:title', meta.title);
        setProperty('og:description', meta.description);
        setProperty('og:url', meta.canonical);
    }, [pathname]);
    return null;
};
